/**
 * The configurable pivot: budget vs actual across two axes the admin picks.
 *
 * A matrix earns its keep only if a cell can be read without a legend, so each cell shows its
 * measures stacked and labelled on hover, variance keeps the favourable/unfavourable colouring
 * used everywhere else, and empty intersections stay blank rather than printing zeros.
 */
import React from 'react';
import { cn } from '../../../lib/utils';
import { money, percent } from './financeFormat';

const MEASURE_LABELS = {
  budget: 'Budget',
  actual: 'Actual',
  variance: 'Variance',
  variance_percent: 'Var %',
};

const BASIS_LABELS = { costs: 'Cost', income: 'Rev', net: 'Net' };

// Keys can be account codes, month stamps or free-text counterparties, so testids are slugified
const slug = (value) =>
  String(value ?? 'none').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'none';

/** Which sides of the ledger this cell should print. */
const basesOf = (cell, basis) => {
  if (!cell) return [];
  if (basis && basis !== 'split') return [basis];
  const bases = [];
  if (cell.has_costs) bases.push('costs');
  if (cell.has_income) bases.push('income');
  if (cell.has_costs && cell.has_income) bases.push('net');
  return bases;
};

const Line = ({ basis, block, measures, currency, showLabel }) => {
  // Colour from the figure the server sent, not a recomputation of it
  const favourable = basis === 'costs' ? block.variance <= 0 : block.variance >= 0;
  const neutral = block.variance_percent !== null && block.variance_percent !== undefined
    && Math.abs(block.variance_percent) < 2;
  const varianceTone = neutral
    ? 'text-foreground-muted'
    : favourable ? 'text-emerald-600' : 'text-red-600';
  const value = (measure) => {
    if (measure === 'variance') {
      return block.variance === 0
        ? '—'
        : `${block.variance > 0 ? '+' : '−'}${money(Math.abs(block.variance), { currency })}`;
    }
    if (measure === 'variance_percent') {
      return block.variance_percent === null || block.variance_percent === undefined
        ? '—'
        : percent(block.variance_percent);
    }
    return money(block[measure], { currency });
  };

  return (
    <div className="flex items-baseline justify-end gap-1.5">
      {showLabel && (
        <span
          className={cn(
            'text-[9px] uppercase tracking-wider',
            basis === 'income' ? 'text-emerald-600' : basis === 'net' ? 'text-foreground' : 'text-foreground-muted'
          )}
        >
          {BASIS_LABELS[basis]}
        </span>
      )}
      <span className="flex flex-col items-end">
        {measures.map((measure) => (
          <span
            key={measure}
            className={cn(
              'tabular-nums',
              measure === 'actual'
                ? cn('text-sm', basis === 'net' ? 'font-semibold' : 'font-medium')
                : measure.startsWith('variance')
                  ? cn('text-xs', varianceTone)
                  : 'text-xs text-foreground-muted'
            )}
          >
            {value(measure)}
          </span>
        ))}
      </span>
    </div>
  );
};

const Cell = ({ cell, measures, currency, basis }) => {
  const bases = basesOf(cell, basis);
  if (!bases.length) return <span className="text-foreground-muted/50">·</span>;
  return (
    <div className="flex flex-col items-end gap-1">
      {bases.map((key) => (
        <Line
          key={key}
          basis={key}
          block={cell[key] || { budget: 0, actual: 0, variance: 0 }}
          measures={measures}
          currency={currency}
          showLabel={bases.length > 1}
        />
      ))}
    </div>
  );
};

export const PivotMatrix = ({ pivot, currency = 'EUR', onDrill }) => {
  if (!pivot || pivot.is_empty) {
    return (
      <p className="text-sm text-foreground-muted px-4 py-10 text-center" data-testid="pivot-empty">
        Nothing to pivot yet. Add budget lines or approve some transactions first.
      </p>
    );
  }

  const measures = pivot.config?.measures || ['budget', 'actual', 'variance'];
  const basis = pivot.config?.basis || 'split';
  const columns = pivot.columns || [];

  return (
    <div className="overflow-x-auto">
      <table className="w-full" data-testid="pivot-matrix">
        <thead>
          <tr className="border-b border-black/[0.08]">
            <th className="sticky left-0 bg-white py-2 pl-4 pr-3 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground-muted">
              {pivot.config?.row_label}
            </th>
            {columns.map((column) => (
              <th
                key={column.key}
                className="py-2 px-3 text-right text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground-muted whitespace-nowrap"
                data-testid={`pivot-col-${slug(column.key)}`}
              >
                {column.label}
              </th>
            ))}
            <th className="py-2 pr-4 pl-3 text-right text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground">
              Total
            </th>
          </tr>
          <tr className="border-b border-black/[0.06]">
            <th className="sticky left-0 bg-white py-1 pl-4 pr-3 text-left text-[10px] text-foreground-muted font-normal">
              {basis === 'split' ? 'Cost / Revenue / Net · ' : ''}
              {measures.map((m) => MEASURE_LABELS[m]).join(' / ')}
            </th>
            {columns.map((column) => (
              <th key={column.key} />
            ))}
            <th />
          </tr>
        </thead>
        <tbody>
          {pivot.rows.map((row) => (
            <tr
              key={row.key}
              className="border-b border-black/[0.04] hover:bg-black/[0.015] transition-colors"
              data-testid={`pivot-row-${slug(row.key)}`}
            >
              <td className="sticky left-0 bg-white py-2.5 pl-4 pr-3">
                <span className="text-sm font-medium">{row.label}</span>
                {row.income_row && (
                  <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                    revenue
                  </span>
                )}
                {row.mixed_row && (
                  <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-black/[0.05] text-foreground-muted">
                    cost + revenue
                  </span>
                )}
              </td>
              {row.cells.map((cell, index) => (
                <td
                  key={columns[index]?.key || index}
                  className={cn('py-2.5 px-3 text-right', cell && onDrill && 'cursor-pointer')}
                  onClick={() => cell && onDrill && onDrill(row, columns[index])}
                  data-testid={`pivot-cell-${slug(row.key)}-${slug(columns[index]?.key)}`}
                >
                  <Cell cell={cell} measures={measures} currency={currency} basis={basis} />
                </td>
              ))}
              <td className="py-2.5 pr-4 pl-3 text-right bg-black/[0.015]">
                <Cell cell={row.total} measures={measures} currency={currency} basis={basis} />
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-black/[0.03]">
            <td className="sticky left-0 bg-[#f7f7f7] py-3 pl-4 pr-3 text-sm font-semibold">Total</td>
            {(pivot.column_totals || []).map((column) => (
              <td key={column.key} className="py-3 px-3 text-right" data-testid={`pivot-total-${slug(column.key)}`}>
                <Cell cell={column.total} measures={measures} currency={currency} basis={basis} />
              </td>
            ))}
            <td className="py-3 pr-4 pl-3 text-right font-semibold" data-testid="pivot-grand-total">
              <Cell cell={pivot.grand_total} measures={measures} currency={currency} basis={basis} />
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default PivotMatrix;
